/**
 * The supplier portal's contract (docs/features/supplier-interface/): one set of supplier pages and actions over
 * our purchase orders, entered two ways (brief §9, "one set of pages, two ways in"):
 *
 * - **by link**: the emailed per-order link. Its token travels in the URL fragment and is sent with each request
 *   as `X-Supplier-Link-Token` (EA §5.11, spec 10), so it never reaches a server log or a Referer. One purchase order, a fixed set of actions while it is
 *   open; once it closes (delivered, received or cancelled) a 60-day closed window allows only reading, invoice
 *   upload and replying to queries; then it expires (spec 06).
 * - **by login**: a supplier person signed in (phase 3, Entra External ID): every purchase order addressed to
 *   their supplier, plus the orders list, invoices and account.
 *
 * Every call carries the `SupplierAccess` it was made with, and the server decides the scope from it. Pages never
 * decide what a supplier may do: each purchase-order line names the actions it allows (`actions`), the order says
 * whether *Accept all* applies, and whether an invoice can be uploaded and until when. A refused credential is
 * opaque: a link read answers `link_not_valid` whatever the cause, and a login read answers `not_found` for an
 * order that is not the supplier's or does not exist, so neither says whether the order exists.
 *
 * Two implementations: the mock (`../mock-data/supplier-portal.ts`) and the real one over the supplier app's routes
 * (`./api/supplier-portal.ts`, `/supplier-api`, EA §5.9; each method maps onto one of spec 10 §2's endpoints), run
 * through one contract suite. The login half and the warehouse picks (`supplier-picks-adapter.ts`) are there too. Writes answer nothing (but *Can't meet the date*, which says whether Pollards accepted the date at once) and the page re-reads, as the customer link pages do. A
 * refused write is an `ApiError`: 400 with field errors in the `.claude/rules/api-validation.md` shape (read with
 * `extractFieldErrors`), or a 409 whose `detail` is the sentence to show.
 *
 * Terms are the brief's glossary: a **purchase order** and its **lines** (1:1 with our order lines, or at worst an
 * order-line version), **deliveries** (despatch is per delivery), **events** with their **source**, and
 * **invoices** with their status from our ledger.
 */

import type { ListOptions, ListResponse } from "./adapter";
import type { ServiceKind } from "./order-to-invoice-types";
import type {
  ProposedDateAnswerResponse,
  QuoteRequestPageResponse,
  QuoteRequestRowOut,
  SupplierGoodsInRow,
  SupplierWarehouse,
} from "./supplier-api-types";

export type { ServiceKind };

// ── Access ──

/** How this request came in. Pages render chrome from it (link: no nav), never what the supplier may do. */
export type SupplierAccess = { via: "link"; token: string } | { via: "login" };

/**
 * The signed-in supplier person, what is waiting on them (the nav counts) and the warehouses their supplier runs
 * for us (empty for most suppliers). The purchase-order counts are present only where the purchase-order half of
 * the portal is served; the warehouse picks half is served wherever `warehouses` is not empty.
 */
export interface SupplierSession {
  supplier_no: string;
  supplier_name: string;
  person_name: string;
  warehouses: SupplierWarehouse[];
  waiting: {
    /** Picks waiting to be picked, across every warehouse. */
    picks_to_pick: number;
    /** Delivery lines still due into the warehouses the supplier operates, across every warehouse. */
    goods_in_due: number;
    /** Purchase orders with a line to accept or reject. */
    orders_to_accept?: number;
    /** Invoices with a query from Pollards awaiting the supplier's reply. */
    invoice_queries?: number;
    /** Quote requests still asked and not locked: the *Quote requests* tab's count. */
    quote_requests_to_answer?: number;
  };
}

/**
 * The login half of supplier access (phase 3). The real implementation sends the browser to the supplier app's
 * sign-in (`/supplier-api/auth/initiate`, its own Entra External ID application and user flow; EA §5.9), which
 * sets an httpOnly session cookie; the mock signs a fixture person in. Pages never see a credential.
 */
export interface SupplierPortalSignInAdapter {
  /** The signed-in person, or null when nobody is. */
  getSession(): Promise<SupplierSession | null>;
  /** Start signing in, coming back to `returnTo` (a path in this app). */
  signIn(returnTo: string): Promise<void>;
  signOut(): Promise<void>;
}

// ── The purchase order page ──

/** Where the order stands, from the supplier's side. `accepted` is "in production" on the orders list. */
export type PurchaseOrderStatus =
  "to_accept" | "accepted" | "despatched" | "closed" | "cancelled";

export type PurchaseOrderLineStatus =
  | "to_accept"
  | "accepted"
  /** "Can't meet the date": the supplier proposed a later date; purchasing decides. */
  | "date_proposed"
  /** The supplier gave a price of its own that Pollards' buying team has to approve first (spec 30 §L2e). */
  | "price_to_approve"
  /** Pollards asked the supplier to cancel the line: it can only confirm (with its costs) or refuse. */
  | "cancel_requested"
  | "rejected"
  | "part_despatched"
  | "despatched"
  | "delivered"
  | "cancelled";

/** Carriers the despatch form offers. Own van asks for a delivery date instead of tracking. */
export const CARRIERS = [
  "dpd",
  "dx",
  "royal_mail",
  "parcelforce",
  "evri",
  "ups",
  "fedex",
  "tnt",
  "dhl_express",
  "dhl_parcel_uk",
  "yodel",
  "inpost",
  "apc_overnight",
  "pall_ex",
  "palletforce",
  "palletways",
  "the_pallet_network",
  "xdp",
  "own_van",
  "other",
] as const satisfies readonly NonNullable<SupplierGoodsInRow["carrier"]>[];

export type Carrier = (typeof CARRIERS)[number];

/** A carrier the API names that `CARRIERS` lacks; `never` while the list is complete (`satisfies` above checks only that
 * every listed carrier exists, so this is the other direction). */
type UnlistedCarrier = Exclude<
  NonNullable<SupplierGoodsInRow["carrier"]>,
  Carrier
>;
export const EVERY_API_CARRIER_IS_LISTED: [UnlistedCarrier] extends [never]
  ? true
  : never = true;

/** What a supplier can do to one line now, decided by the server from the line's state and the access scope. */
export type LineAction =
  | "accept"
  /** Accept the line at a delivered price of the supplier's own (spec 30 §L2e). */
  | "accept_at_price"
  | "propose_date"
  | "reject"
  | "mark_despatched"
  | "upload_proof"
  /** Confirm Pollards' request to cancel the line (with the costs incurred), or refuse it (with why). */
  | "answer_cancellation"
  /** Accept the change Pollards made to the line after the supplier had the order, or say it can't make it. */
  | "answer_amendment";

/** The supplier's answer to a change Pollards made after it had the order: accepted, or refused with why. */
export type AmendmentAnswerInput =
  { outcome: "accepted" } | { outcome: "refused"; reason: string };

/** A change Pollards made to the line after the supplier had the order, which it has not answered yet. */
export interface ChangeToAnswer {
  /** The day Pollards made the change. */
  changed_on: string;
  /** What changed: only the deliveries change after sending today. */
  change: "deliveries";
  /** The day Pollards asks the supplier to answer by; null when no day was asked. */
  answer_by: string | null;
}

/** The supplier's answer to a request to cancel a line: confirmed with the costs it incurred (pence, ex VAT), or refused. */
export type CancellationAnswerInput =
  | { outcome: "confirmed"; costs_incurred_pence: number }
  | { outcome: "refused"; reason: string };

/**
 * A file the supplier can download: a line's artwork. `download_url` is fetched with the access. A mailing's data
 * file is not one: mailing data is handled off-system (Ben, 8 Oct), so CIS neither stores nor serves it.
 */
export interface SupplierFile {
  file_name: string;
  download_url: string;
  /** A short note beside the file name, when the server gives one. */
  note: string | null;
}

/** Our check of the supplier's proof (brief decision 8): automatic over a threshold, otherwise approved directly. */
export type ProofState =
  "to_upload" | "checking" | "approved" | "changes_needed";

export interface LineProof {
  state: ProofState;
  decided_on: string | null;
  /** Pollards' note when changes are needed. */
  note: string | null;
}

/** Postage paid on whose account (brief decision 9: a per-supplier default, overridable per order). */
export type PostageAccount = "supplier" | "pollards" | "customer";

export interface ServiceActivity {
  /** The dictionary's activity code (`enclose`, `kitting` …). A fulfilment with `kitting` is labelled "Kitting". */
  code: string;
  name: string;
  /** The activity's parameters, as the dictionary words them ("C5, 3 items"). */
  detail: string | null;
}

/**
 * What service wording is decided by: the dictionary's service kind (brief decision 9, one name per concept) and
 * its activities, since kitting is an activity of fulfilment, not a kind.
 */
export interface ServiceRef {
  kind: ServiceKind;
  activities: Pick<ServiceActivity, "code">[];
}

export interface ServiceLine {
  kind: ServiceKind;
  activities: ServiceActivity[];
  postage: { service: string; account: PostageAccount } | null;
}

export interface LineVersion {
  name: string;
  quantity: number;
}

/** One delivery (despatch is per delivery, so a split delivery carries its own tracking). */
export interface Delivery {
  delivery_no: number;
  quantity: number;
  /** Null for a service line (posted, kitted, installed). */
  carrier: Carrier | null;
  /** The carrier's name when `carrier` is `other`. */
  carrier_name: string | null;
  tracking_number: string | null;
  despatched_on: string;
  /** Own van: the day it will arrive. */
  deliver_on: string | null;
  /** Proof of posting, or an installation's sign-off. */
  evidence_file_name: string | null;
  delivered_on: string | null;
}

/**
 * A kit's line (V1 completion R5): the kits to pack and the places they go. The places are read a page at a time
 * (`getKitDrops` at `drops_url`), and the manifest of what each pack holds, where it goes, is a file
 * (`downloadFile` at `manifest_url`), because a kit can have 2,000 drops.
 */
export interface SupplierKit {
  kits: number;
  drops: number;
  drops_url: string;
  manifest_url: string;
}

export type KitDropStatus =
  "to_despatch" | "despatched" | "delivered" | "cancelled";

/** One place a pack goes. Its `number` is the manifest's Drop. */
export interface SupplierKitDrop {
  number: number;
  /** "Shop 0412, Clifton". */
  place: string;
  address_lines: string[];
  postcode: string | null;
  kits: number;
  status: KitDropStatus;
  /** "To despatch", "Despatched 13 Oct", "Delivered 14 Oct". */
  status_label: string;
}

export interface PurchaseOrderLine {
  line_no: number;
  /** What to make, in our white-label words. */
  description: string;
  quantity: number;
  /** "pieces" for a mailing; null for an item. */
  quantity_unit: string | null;
  /** Their own reference for the quote this line was priced from ("Your quote"); absent for a line not priced from their quote. */
  quote_reference?: string | null;
  /** Their customer's PO number, to put on the delivery note; absent when the customer gave none. */
  customer_order_number?: string | null;
  /** The supplier's own product for this spec, where mapped. */
  supplier_product: { name: string; reference: string | null } | null;
  /** Empty for a single-version line. */
  versions: LineVersion[];
  artwork: SupplierFile[];
  service: ServiceLine | null;
  /** Null when no proof is asked of the supplier for this line. */
  proof: LineProof | null;
  /** `deliver_by` is null when the order names no required date. */
  deliver_to: { address: string; deliver_by: string | null };
  status: PurchaseOrderLineStatus;
  /** "Can't meet the date": the earliest date the supplier proposed. */
  proposed_date: string | null;
  /** With `cancel_requested`: the day Pollards asked for an answer by; null when no day was asked. */
  cancel_answer_by?: string | null;
  /** A change Pollards made after the supplier had the order that it has not answered; null when there is none. */
  change_to_answer?: ChangeToAnswer | null;
  rejection_reason: string | null;
  deliveries: Delivery[];
  /** Still to despatch (quantity less every delivery so far). */
  quantity_to_send: number;
  actions: LineAction[];
  /** What Pollards pays for the line as sent, ex VAT, in pence: the price accepted, or answered with a price of its own. */
  cost_pence?: number;
  /** A kit's line; absent or null for any other. */
  kit?: SupplierKit | null;
}

/** Goods arriving at a service supplier for this order (goods follow the service, brief decision 9). */
export interface InboundGoods {
  inbound_id: string;
  from_supplier: string;
  description: string;
  quantity: number;
  /** Null until something dates them (a promised or required date, or a despatch). */
  expected_on: string | null;
  status: "in_production" | "despatched" | "delivered";
  carrier: Carrier | null;
  tracking_number: string | null;
}

/** The one next thing, decided by the server; the wording table words it. */
export type NeedsYou =
  | {
      kind: "accept_lines";
      line_count: number;
      already_accepted_count: number;
      by: string | null;
    }
  | {
      kind: "despatch";
      line_count: number;
      /** Null when no line has a date to despatch by. */
      by: string | null;
      /** A service order: when the goods for it arrive. */
      goods_arriving_on: string | null;
    }
  | { kind: "awaiting_pollards"; line_count: number }
  | { kind: "upload_invoice"; until: string | null };

export type PurchaseOrderEventKind =
  | "sent"
  | "line_acknowledged"
  | "line_rejected"
  | "date_change_proposed"
  | "date_change_acceptance"
  | "despatched"
  | "delivered"
  | "proof_submitted"
  | "proof_decided"
  | "invoice_uploaded"
  | "shortage_reported"
  | "cancelled"
  | "line_cancelled"
  | "line_destinations_changed";

/** How an event arrived (EA §5.4). */
export type PurchaseOrderEventSource =
  | "manual"
  | "link"
  | "portal"
  | "file"
  | "api"
  | "email"
  | "agent"
  | "carrier"
  | "system";

/**
 * One entry in the order's activity: a purchase-order event, or `opened`, a link open that views compose into the
 * activity from supplier access's audit (it is not a purchase-order event).
 */
export type ActivityEntryKind = PurchaseOrderEventKind | "opened";

export interface ActivityEntry {
  at: string;
  kind: ActivityEntryKind;
  source: PurchaseOrderEventSource;
  /** The supplier person, or null for Pollards and automatic events. */
  actor_name: string | null;
  line_nos: number[];
  /** `sent`: how the order went to the supplier. */
  sent_via: "email" | "portal" | null;
  /** `date_change_*`: the date. */
  date: string | null;
  /** `line_rejected`, `date_change_proposed`, `shortage_reported`: the supplier's words. */
  reason: string | null;
  /** `date_change_acceptance`, `proof_decided`. */
  outcome: "accepted" | "declined" | "approved" | "changes_needed" | null;
  /** `despatched`. */
  carrier: Carrier | null;
  carrier_name: string | null;
  tracking_number: string | null;
  /** `invoice_uploaded`. */
  invoice_ref: string | null;
}

/** The trading company that placed an order, as its purchase order document names it ("Placed by"). */
export interface PlacedBy {
  legal_name: string;
  /** Null until the company has one. */
  company_number: string | null;
  registered_in: string;
  registered_office_lines: string[];
  vat_number: string;
  /** The one-line wording the order email and its PDF use. */
  footer_line: string;
}

export interface PurchaseOrderPage {
  purchase_order_no: string;
  supplier_name: string;
  status: PurchaseOrderStatus;
  /** A bought-in service order (a mailing, fulfilment, installation …): its wording follows the service. */
  service: ServiceRef | null;
  raised_on: string;
  /** Null when the order names no required date. */
  ready_by: string | null;
  /** Delivered or cancelled, when closed. */
  closed_on: string | null;
  pollards_contact: { name: string; role: string };
  /** The trading company that placed the order; null only when One cannot find it. */
  placed_by: PlacedBy | null;
  needs_you: NeedsYou | null;
  can_accept_all: boolean;
  /** What the supplier's next price increase this month would be called ("2nd"); null when no line can be given a price. */
  next_price_increase?: string | null;
  lines: PurchaseOrderLine[];
  arriving: InboundGoods[];
  /** Whether a shortage on arrival can be reported (a service order with goods arriving). */
  can_report_shortage: boolean;
  invoicing: {
    can_upload: boolean;
    /** A closed order: invoices can be uploaded until this date. */
    upload_until: string | null;
    invoices: SupplierInvoice[];
  };
  activity: ActivityEntry[];
  /** The server's today, for date defaults. */
  today: string;
}

/** A purchase order read. Link refusals are one opaque answer; a login sees "not found" for not-yours-or-absent. */
export type PurchaseOrderRead =
  | { kind: "ok"; page: PurchaseOrderPage }
  | { kind: "link_not_valid" }
  | { kind: "not_found" }
  | { kind: "signed_out" };

// ── The orders list (login only) ──

export type PurchaseOrderListTab =
  "to_accept" | "in_production" | "despatched" | "closed";

export const PURCHASE_ORDER_LIST_TABS: readonly PurchaseOrderListTab[] = [
  "to_accept",
  "in_production",
  "despatched",
  "closed",
];

export interface PurchaseOrderListOptions extends ListOptions {
  tab: PurchaseOrderListTab;
  /** Only orders past their required date (the "needing attention" section). */
  overdueOnly?: boolean;
}

export interface PurchaseOrderListItem {
  purchase_order_no: string;
  line_count: number;
  summary: string;
  ready_by: string | null;
  status: PurchaseOrderStatus;
  lines_to_accept: number;
  /** Lines Pollards asked the supplier to cancel that still wait for its answer. */
  lines_cancel_requested: number;
  /** Days past the required date while not yet despatched; null when not overdue. */
  overdue_days: number | null;
  closed_on: string | null;
}

export interface PurchaseOrderList extends ListResponse<PurchaseOrderListItem> {
  /** Per-tab counts under the same search, so the tabs show what is waiting without a query each. */
  tabCounts: Record<PurchaseOrderListTab, number>;
}

// ── Invoices ──

/** The invoice's status from our ledger (brief decision 5). */
export type InvoiceLedgerStatus = "received" | "approved" | "paid" | "query";

export type InvoiceGroup = "in_progress" | "paid";

export interface InvoiceQueryMessage {
  author: "pollards" | "supplier";
  /** "Finance, Pollards" or the supplier person. */
  author_name: string;
  at: string;
  text: string;
  attachment_file_name: string | null;
}

export interface SupplierInvoice {
  /** The supplier's own invoice number. */
  invoice_ref: string;
  purchase_order_nos: string[];
  /** Decimal pounds, inc VAT, as the supplier gave it. */
  total_inc_vat: string;
  received_on: string;
  ledger_status: InvoiceLedgerStatus;
  due_on: string | null;
  paid_on: string | null;
  /** Finance's query and the replies, oldest first; null when never queried. */
  query: { messages: InvoiceQueryMessage[]; can_reply: boolean } | null;
}

export interface InvoiceListOptions extends ListOptions {
  group: InvoiceGroup;
}

export interface InvoiceUploadOptions {
  purchase_order_nos: string[];
  today: string;
}

export interface InvoiceUploadInput {
  file: File | null;
  purchase_order_nos: string[];
  invoice_ref: string;
  total_inc_vat: string;
  invoice_date: string;
}

// ── Account (login only; read-only in v1, staff manage people) ──

export interface SupplierAccount {
  supplier_name: string;
  supplier_no: string;
  /** Null when the supplier's orders do not reach it through the portal yet. */
  orders_reach_you: { channel: "email_with_link"; address: string } | null;
  /** Each login's label, else its address (07, section 11). */
  people: { name: string }[];
  pollards_contact: { name: string; role: string };
}

// ── Writes ──

export interface ProposeDateInput {
  earliest_on: string;
  reason: string;
}

/**
 * What Pollards has answered to a proposed date: `accepted_date` is the date when it still meets the customer's
 * date and was accepted at once; null when a person at Pollards will decide.
 */
export type ProposedDateAnswer = ProposedDateAnswerResponse;

/** One line the supplier accepts at a price of its own: its delivered price for the whole line, ex VAT, in pence. */
export interface PricedLineInput {
  line_no: number;
  cost_pence: number;
}

/** What Pollards did with the prices: took them, or its buying team will approve them first. */
export interface AcceptedAtPrice {
  outcome: "accepted" | "held";
  /** The price the supplier gave for the lines, ex VAT, in pence. */
  price_pence: number;
}

export interface DespatchInput {
  /** A part delivery leaves the rest of the line to send later. */
  is_part: boolean;
  quantity: number;
  /** Required for an item line; null for a service line. */
  carrier: Carrier | null;
  carrier_name: string;
  tracking_number: string;
  despatched_on: string;
  /** Own van: when it will arrive. */
  deliver_on: string;
  /** Proof of posting (mailing) or sign-off (installation). */
  evidence: File | null;
}

export interface ShortageInput {
  inbound_id: string;
  quantity_received: number;
  note: string;
}

// ── Quote requests (supplier interface spec 33) ──

/**
 * One quote request as its supplier sees it (the server's page): what we need, the prices already sent, what can be
 * done now (`can_send`, `can_change`, `can_decline`) and the writes' URLs. It carries no customer, quote number, line
 * reference or title of ours (spec 31, invariant 1).
 */
export type QuoteRequestPage = QuoteRequestPageResponse;

/** One row of the *Quote requests* list (logins only). */
export type QuoteRequestRow = QuoteRequestRowOut;

/** A quote request read. A link's refusal is one opaque answer; a login sees "not found" for not-yours-or-absent. */
export type QuoteRequestPageResult =
  | { kind: "ok"; page: QuoteRequestPage }
  | { kind: "link_not_valid" }
  | { kind: "not_found" }
  | { kind: "signed_out" };

/** One quantity's answer: a delivered price ex VAT in pence, or Can't quote (the staff command's price row). */
export interface QuotePriceRowInput {
  quantity: number;
  /** Pence; null when `not_quoted`. */
  cost: number | null;
  not_quoted: boolean;
}

/**
 * *Send our prices* and *Change our prices*: the `answer` JSON field of the multipart write
 * (`SendQuotePricesAnswer` in the supplier app). Hand-written because a form field's JSON is not part of the OpenAPI
 * schema; field errors share its paths (`prices.N.cost`, `their_reference`, `valid_until`, `despatch_by`).
 * `request_id` is made once per submission and reused if the same submission is retried.
 */
export interface QuoteRequestAnswer {
  prices: QuotePriceRowInput[];
  their_reference: string;
  /** "2026-11-20". */
  valid_until: string;
  /** "2026-11-06", when given. */
  despatch_by: string | null;
  request_id: string;
}

export interface SupplierPortalDataAdapter {
  getQuoteRequest(
    access: SupplierAccess,
    supplierQuoteNo: string,
  ): Promise<QuoteRequestPageResult>;
  /** The supplier's requests, latest asked first, a page at a time (logins only). */
  listQuoteRequests(page: number): Promise<ListResponse<QuoteRequestRow>>;
  /** Refused as the server refuses: field errors (400), or the request closed or already answered (409). */
  sendQuotePrices(
    access: SupplierAccess,
    supplierQuoteNo: string,
    answer: QuoteRequestAnswer,
    quoteDocument?: File,
  ): Promise<void>;
  declineQuoteRequest(
    access: SupplierAccess,
    supplierQuoteNo: string,
    reason: string,
    requestId: string,
  ): Promise<void>;
  /** One of the request's files, or the supplier's own quote PDF, for the shell to save. */
  downloadQuoteRequestFile(
    access: SupplierAccess,
    supplierQuoteNo: string,
    fileId: string,
  ): Promise<Blob>;

  getPurchaseOrder(
    access: SupplierAccess,
    purchaseOrderNo: string,
  ): Promise<PurchaseOrderRead>;
  /** *Accept* (one line) and *Accept all* (every line still waiting): one event per line. */
  acceptLines(
    access: SupplierAccess,
    purchaseOrderNo: string,
    lineNos: number[],
  ): Promise<void>;
  /**
   * *Accept at a different price*: each line with the supplier's delivered price for it (pence, ex VAT). Answers
   * whether Pollards took the price (`accepted`) or its buying team approves it first (`held`).
   */
  acceptLinesAtPrice(
    access: SupplierAccess,
    purchaseOrderNo: string,
    prices: PricedLineInput[],
  ): Promise<AcceptedAtPrice>;
  proposeDate(
    access: SupplierAccess,
    purchaseOrderNo: string,
    lineNo: number,
    input: ProposeDateInput,
  ): Promise<ProposedDateAnswer>;
  rejectLine(
    access: SupplierAccess,
    purchaseOrderNo: string,
    lineNo: number,
    reason: string,
  ): Promise<void>;
  /** Answer Pollards' request to cancel the line: confirm it with the costs incurred, or refuse it with a reason. */
  answerCancellation(
    access: SupplierAccess,
    purchaseOrderNo: string,
    lineNo: number,
    input: CancellationAnswerInput,
  ): Promise<void>;
  /** Answer the change Pollards made to the line after you had the order: accept it, or refuse it with a reason. */
  answerAmendment(
    access: SupplierAccess,
    purchaseOrderNo: string,
    lineNo: number,
    input: AmendmentAnswerInput,
  ): Promise<void>;
  markDespatched(
    access: SupplierAccess,
    purchaseOrderNo: string,
    lineNo: number,
    input: DespatchInput,
  ): Promise<void>;
  uploadProof(
    access: SupplierAccess,
    purchaseOrderNo: string,
    lineNo: number,
    file: File,
  ): Promise<void>;
  reportShortage(
    access: SupplierAccess,
    purchaseOrderNo: string,
    input: ShortageInput,
  ): Promise<void>;
  /** One page of a kit line's drops (`kit.drops_url`), searched on the server (`options.q`). */
  getKitDrops(
    access: SupplierAccess,
    dropsUrl: string,
    options: ListOptions,
  ): Promise<ListResponse<SupplierKitDrop>>;
  /** A file a read named (`download_url`, or a kit's `manifest_url`), fetched with the access, for the shell to save. */
  downloadFile(
    access: SupplierAccess,
    downloadUrl: string,
  ): Promise<{ file_name: string; blob: Blob }>;

  listPurchaseOrders(
    access: SupplierAccess,
    options: PurchaseOrderListOptions,
  ): Promise<PurchaseOrderList>;
  listInvoices(
    access: SupplierAccess,
    options: InvoiceListOptions,
  ): Promise<ListResponse<SupplierInvoice>>;
  /** What the invoice upload form offers now: the purchase orders it can be for, and the server's today. */
  getInvoiceUploadOptions(
    access: SupplierAccess,
  ): Promise<InvoiceUploadOptions>;
  uploadInvoice(
    access: SupplierAccess,
    input: InvoiceUploadInput,
  ): Promise<void>;
  replyToInvoiceQuery(
    access: SupplierAccess,
    invoiceRef: string,
    input: { text: string; attachment: File | null },
  ): Promise<void>;
  getAccount(access: SupplierAccess): Promise<SupplierAccount>;
}
